import React from "react";
import { Zap, UserMinus } from "lucide-react";
import PageCard from "./PageCard";
import { SkeletonBox } from "../../../components/SkeletonLoader";

/**
 * Middle panel: one PageCard per registry page. Pages/features/columns are
 * rendered EXCLUSIVELY from GET /admin-levels/registry — nothing hardcoded.
 *
 * Target-agnostic: the "target" is whatever is being configured — a user
 * (Users tab) or a shared level (Levels tab). `headerActions` lets each tab
 * inject its own buttons (Visibility Control, Assign Employees, …).
 */
const PageMatrix = ({
  registry, hasTarget, levelLoading,
  pageStates, features, hiddenColumns,
  onSetPageState, onToggleFeature, onToggleColumn,
  canRemoveLevel, onRemoveLevel,
  headerActions,
  emptyTitle, emptyHint,
  theme, language, t, targetLabel,
  disabled = false,
}) => {
  const headColor = theme === "dark" ? "#f1f5f9" : "#1e293b";
  const subColor = theme === "dark" ? "#94a3b8" : "#64748b";

  if (!hasTarget) {
    return (
      <div style={{
        display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center", minHeight: "320px", textAlign: "center",
        color: subColor, padding: "24px",
      }}>
        <div style={{
          width: "56px", height: "56px", borderRadius: "18px",
          display: "flex", alignItems: "center", justifyContent: "center",
          marginBottom: "16px",
          background: theme === "dark" ? "rgba(46,124,186,0.15)" : "rgba(46,124,186,0.08)",
        }}>
          <Zap size={26} style={{ color: "#2e7cba" }} />
        </div>
        <div style={{ fontSize: "15px", fontWeight: 600, color: headColor, marginBottom: "6px" }}>
          {emptyTitle || t.selectUserPrompt}
        </div>
        <div style={{ fontSize: "12.5px", maxWidth: "340px", lineHeight: 1.6 }}>
          {emptyHint || t.selectUserHint}
        </div>
      </div>
    );
  }

  if (levelLoading || !registry) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        <SkeletonBox width="220px" height="20px" style={{ marginBottom: "8px" }} />
        {[1, 2, 3, 4, 5].map((i) => (
          <SkeletonBox key={i} width="100%" height="76px" style={{ borderRadius: "14px" }} />
        ))}
      </div>
    );
  }

  return (
    <div>
      <div style={{
        display: "flex", alignItems: "baseline", justifyContent: "space-between",
        flexWrap: "wrap", gap: "8px", marginBottom: "16px",
      }}>
        <div>
          <h4 style={{ margin: 0, fontSize: "16px", fontWeight: 700, color: headColor }}>
            {t.pagesTitle}
          </h4>
          <div style={{ fontSize: "12px", color: subColor, marginTop: "3px" }}>
            {t.configuringFor} {targetLabel}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
          <span style={{
            fontSize: "11.5px", fontWeight: 600, padding: "4px 12px", borderRadius: "999px",
            background: theme === "dark" ? "rgba(16,185,129,0.14)" : "rgba(16,185,129,0.1)",
            color: theme === "dark" ? "#34d399" : "#059669",
          }}>
            {t.changesSaveInstantly}
          </span>
          {headerActions}
          {canRemoveLevel && (
            <button
              type="button"
              onClick={onRemoveLevel}
              style={{
                display: "inline-flex", alignItems: "center", gap: "6px",
                fontSize: "11.5px", fontWeight: 600, padding: "4px 12px",
                borderRadius: "999px", cursor: "pointer", fontFamily: "inherit",
                transition: "all 0.18s ease",
                background: theme === "dark" ? "rgba(239,68,68,0.14)" : "rgba(239,68,68,0.08)",
                border: `1px solid ${theme === "dark" ? "rgba(239,68,68,0.3)" : "rgba(239,68,68,0.2)"}`,
                color: theme === "dark" ? "#f87171" : "#dc2626",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = theme === "dark" ? "rgba(239,68,68,0.25)" : "rgba(239,68,68,0.15)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = theme === "dark" ? "rgba(239,68,68,0.14)" : "rgba(239,68,68,0.08)";
              }}
            >
              <UserMinus size={13} />
              {t.removeAdminLevelBtn}
            </button>
          )}
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        {(registry.pages || []).map((page) => (
          <PageCard
            key={page.key}
            page={page}
            state={pageStates[page.key]}
            enabledFeatures={features[page.key]}
            hiddenColumns={hiddenColumns[page.key]}
            onSetPageState={onSetPageState}
            onToggleFeature={onToggleFeature}
            onToggleColumn={onToggleColumn}
            disabled={disabled}
            theme={theme} language={language} t={t}
          />
        ))}
      </div>
    </div>
  );
};

export default PageMatrix;
